import {
	Button,
	Card,
	CardBody,
	CardHeader,
	CardHeading,
	DateField,
	DrawerFooter,
	DrawerFooterActions,
	Icon,
	IconButton,
	Row,
	SubmitButton,
} from "@dgs/core";
import { isAfter } from "date-fns";
import { FieldArray, Form, Formik, FormikErrors } from "formik";
import { Fragment } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { EventCategoryMultiSelectField } from "../../../shared/ui/EventCategoryMultiSelectField";
import { EventFormatMultiSelectField } from "../../../shared/ui/EventFormatMultiSelectField";
import { EventTypeMultiSelectField } from "../../../shared/ui/EventTypeMultiSelectField";
import { IndustryMultiSelectField } from "../../../shared/ui/IndustryMultiSelectField";
import { OrganizationalUnitMultiSelectField } from "../../../shared/ui/OrganizationalUnitMultiSelectField";
import { OrganizerMultiSelectField } from "../../../shared/ui/OrganizerMultiSelectField";
import { RegionMultiSelectField } from "../../../shared/ui/RegionMultiSelectField";
import { TagMultiSelectField } from "../../../shared/ui/TagSelectField";
import { TargetGroupMultiSelectField } from "../../../shared/ui/TargetGroupMultiSelectField";
import { AttributeType, EventAnalyticsFilterValues } from "./analyticsTypes";
import { EventTypeRequirementSelect } from "./AttributeSelect";
import { initialEventAnalyticsFilter } from "./shared";

const StyledBody = styled(CardBody)`
	display: grid;
	grid-template-columns: 1fr 1fr max-content;
	column-gap: ${({ theme }) => theme.spacing(4)};
	align-items: center;
`;

const Span = styled.div<{ $cols?: number }>`
	grid-column: span ${({ $cols = 2 }) => $cols};
`;

const StyledRow = styled(Row)`
	margin-bottom: ${({ theme }) => theme.spacing(4)};
`;

const InfoIcon = styled(Icon)`
	width: 20px;
	height: 20px;
`;

const StyledFooterActions = styled(DrawerFooterActions)`
	justify-content: end;
`;

interface Props {
	filter: EventAnalyticsFilterValues;
	onSubmit: (values: EventAnalyticsFilterValues) => void;
}

export function AnalyticsFilter({ filter, onSubmit }: Props) {
	const { t } = useTranslation();

	const validate = (values: EventAnalyticsFilterValues) => {
		const errors: FormikErrors<EventAnalyticsFilterValues> = {};

		if (!values.start) {
			errors.start = t("Field is required");
		}
		if (!values.end) {
			errors.end = t("Field is required");
		}
		if (values.start && values.end && isAfter(values.start, values.end)) {
			errors.end = t("The end date must be greater or equal to the start date");
		}

		if (values.attributes && values.attributes.length > 0) {
			const attributeErrors: any[] = [];

			values.attributes.forEach((attribute, index) => {
				const attributeError: { value?: string } = {};

				if (attribute.type !== null && (attribute.value === null || attribute.value.length === 0)) {
					attributeError.value = t("Field is required");
					attributeErrors[index] = attributeError;
				}
			});

			if (attributeErrors.length > 0) {
				errors.attributes = attributeErrors;
			}
		}

		return errors;
	};

	return (
		<Card>
			<Formik initialValues={filter} onSubmit={onSubmit} validate={validate}>
				{({ values, resetForm }) => (
					<Form>
						<CardHeader>
							<CardHeading>{t("Filter")}</CardHeading>
						</CardHeader>
						<StyledBody>
							<Span $cols={3}>
								<StyledRow $gap={2}>
									<InfoIcon $icon="info" />
									<span>
										{t(
											"These filters are applied to the entire page. All displayed content is adjusted and updated accordingly.",
										)}
									</span>
								</StyledRow>
							</Span>
							<DateField name="start" label={t("Start")} required={true} />
							<Span>
								<DateField name="end" label={t("End")} required={true} />
							</Span>
							<FieldArray
								name="attributes"
								render={({ push, remove }) => (
									<>
										{values.attributes.map((attribute, index) => (
											<Fragment key={index}>
												<EventTypeRequirementSelect
													label={t("Attribute")}
													name={`attributes.${index}.type`}
													disabled={attribute.value !== null && attribute.value.length !== 0}
												/>
												{attribute.type ? (
													getSelectField(attribute.type, `attributes.${index}.value`, t("Value"))
												) : (
													<span />
												)}
												<IconButton title={t("Delete attribute")} icon="delete" onClick={() => remove(index)} />
											</Fragment>
										))}
										<Span $cols={3}>
											<Button
												type="button"
												prefix={<Icon $icon="plus" />}
												onClick={() => push({ name: null, value: null })}
											>
												{t("Add attribute")}
											</Button>
										</Span>
									</>
								)}
							/>
						</StyledBody>
						<DrawerFooter>
							<StyledFooterActions>
								<Button type="button" onClick={() => resetForm(initialEventAnalyticsFilter as any)}>
									{t("Reset")}
								</Button>
								<SubmitButton>{t("Submit")}</SubmitButton>
							</StyledFooterActions>
						</DrawerFooter>
					</Form>
				)}
			</Formik>
		</Card>
	);
}

function getSelectField(type: AttributeType, name: string, label: string) {
	switch (type) {
		case AttributeType.eventType:
			return <EventTypeMultiSelectField name={name} label={label} />;
		case AttributeType.organizationalUnits:
			return <OrganizationalUnitMultiSelectField name={name} label={label} />;
		case AttributeType.industries:
			return <IndustryMultiSelectField name={name} label={label} />;
		case AttributeType.organizer:
			return <OrganizerMultiSelectField name={name} label={label} />;
		case AttributeType.format:
			return <EventFormatMultiSelectField name={name} label={label} />;
		case AttributeType.category:
			return <EventCategoryMultiSelectField name={name} label={label} />;
		case AttributeType.targetGroup:
			return <TargetGroupMultiSelectField name={name} label={label} />;
		case AttributeType.region:
			return <RegionMultiSelectField name={name} label={label} />;
		case AttributeType.tags:
			return <TagMultiSelectField name={name} label={label} />;
	}
}
